"use client";

import AiLabel from "@/components/ai-label";
import { CustomCategory } from "@/components/category";
import { Content } from "@/services/content/types";
import { ITEM_FEED_LIMIT } from "@/utils/constants";
import groupItemsByDate from "@/utils/groupItemsByDate";
import { createImageSizes, createImageUrl } from "@/utils/image";
import { suppressNextSkipLinkFocusReset } from "@/utils/skip-link-focus";
import { prefixSlug } from "@/utils/url";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import FormatDate from "./format-date";

type ItemFeedProps = {
  limit?: number;
  items: Content.Entry[];
  total?: number;
  initialPage?: number;
  /** Base path for path-based pagination, e.g. "/av/martin". When set,
   *  page 1 links to `basePath`, page 2+ links to `basePath/2`. */
  basePath?: string;
  groupByDate?: boolean;
  /** Overrides the "full page means more" guess, e.g. for merged feeds
   *  where filtering can shorten a page. */
  hasMore?: boolean;
  loadMore: (page: number) => Promise<
    | (Content.Response<Content.Entry> & {
        hasMore?: boolean;
        /** Page to request next, when the loader skipped empty pages. */
        nextPage?: number;
      })
    | undefined
  >;
};

type ItemProps = {
  item: {
    slug?: string;
    createdAt: string;
    categoryName?: string;
    categoryColor?: Content.CategoryColors;
    title?: string;
    excerpt?: string;
    image?: Content.Asset;
    type?: string;
    fulldate?: boolean;
  };
};

export const Item = (props: ItemProps) => {
  const isPodcast = props.item.type === "podcast-episode";
  const isPlay = props.item.type === "play-episode";
  const isToday =
    new Date(props.item.createdAt).toDateString() === new Date().toDateString();
  return (
    <li className="py-5 last:pb-0">
      <Link
        className="group flex space-x-6 items-start"
        href={props.item.slug!}
      >
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
            <span className="font-noto-sans text-sm text-gray-400">
              {props.item.fulldate && !isToday ? (
                <>
                  <FormatDate date={props.item.createdAt} format="relative" />{" "}
                  <FormatDate date={props.item.createdAt} format="time" />
                </>
              ) : (
                <FormatDate date={props.item.createdAt} format="time" />
              )}
            </span>

            {props.item.categoryName && (
              <CustomCategory
                color={props.item.categoryColor}
                name={props.item.categoryName}
              />
            )}
          </div>

          {props.item.title && (
            <h3 className="text-lg md:text-xl font-semibold group-hover:underline">
              {props.item.title}
            </h3>
          )}

          {props.item.excerpt && (
            <span className="block mt-4 text-gray-400 line-clamp-3">
              {props.item.excerpt}
            </span>
          )}
        </div>
        {props.item.image?.fields.file?.url && (
          <div
            className={classNames("relative flex-shrink-0 mt-2", {
              "w-[80px] h-[80px] md:w-[180px] md:h-[180px]": isPodcast,
              "w-[120px] h-[68px] md:w-[180px] md:h-[100px]": !isPodcast,
            })}
          >
            <Image
              fill
              sizes={createImageSizes({
                desktop: "33vw",
                tablet: "33vw",
                mobile: "33vw",
              })}
              src={createImageUrl(props.item.image.fields.file.url, {
                crop: isPodcast ? "1:1" : "16:9",
              })}
              alt={props.item.image.fields.alt || ""}
              className=""
            />
            <AiLabel image={props.item.image} size="sm" />
          </div>
        )}
      </Link>
    </li>
  );
};

const ItemFeed = (props: ItemFeedProps) => {
  const startPage = props.initialPage ?? 1;
  const limit = props.limit || ITEM_FEED_LIMIT;

  const [page, setPage] = useState(startPage);
  const [items, setItems] = useState<Content.Entry[]>(props.items);
  const [hasMore, setHasMore] = useState(
    props.hasMore ?? props.items.length >= limit,
  );
  const [isLoading, setIsLoading] = useState(false);

  const feedRef = useRef<HTMLDivElement | null>(null);
  const previousItemCount = useRef(items.length);

  useEffect(() => {
    const previousCount = previousItemCount.current;
    previousItemCount.current = items.length;

    if (items.length <= previousCount) return;

    const newItems = feedRef.current?.querySelectorAll("li");
    const firstNewItem = newItems?.[previousCount];
    firstNewItem?.querySelector<HTMLElement>("a")?.focus();
  }, [items]);

  const fetchItems = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const res = await props.loadMore(page);

      if (res?.items) {
        const more = res.hasMore ?? res.items.length >= limit;
        const nextPage = res.nextPage ?? page + 1;

        setHasMore(more);
        setPage(nextPage);
        const existingIds = new Set(items.map((item) => item.sys.id));
        setItems([
          ...items,
          ...res.items.filter((item) => !existingIds.has(item.sys.id)),
        ]);

        const nextHref = props.basePath
          ? nextPage === 1
            ? props.basePath
            : `${props.basePath}/${nextPage}`
          : (() => {
              const url = new URL(window.location.href);
              url.searchParams.set("page", String(nextPage));
              return url.toString();
            })();

        if (props.basePath) {
          // basePath pagination changes the URL's pathname, which Next.js
          // picks up via usePathname() — without this, SkipLink treats it
          // like a real page navigation and steals focus back to <body>.
          suppressNextSkipLinkFocusReset();
        }
        window.history.pushState(null, "", nextHref);
      } else {
        setHasMore(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const groupedItems = props.groupByDate ? groupItemsByDate(items) : null;
  const renderItem = (item: Content.Entry) => {
    if (item.sys.contentType.sys.id === "article") {
      const article = item as Content.Article;
      return (
        <Item
          key={article.sys.id}
          item={{
            slug: prefixSlug(
              article.fields.slug,
              article.sys.contentType.sys.id,
            ),
            fulldate: !props.groupByDate,
            title: article.fields.title,
            image: article.fields.images?.[0],
            categoryName: article.fields.categories?.[0]?.fields?.name,
            categoryColor:
              article.fields.categories?.[0]?.fields?.color ||
              article.fields.categories?.[0]?.fields?.parentCategory?.fields
                ?.color,
            createdAt: article.sys.createdAt,
            type: article.sys.contentType.sys.id,
          }}
        />
      );
    }

    if (item.sys.contentType.sys.id === "play-episode") {
      const playEpisode = item as Content.Play;
      const playShow = playEpisode.fields["play-show"];
      return (
        <Item
          key={playEpisode.sys.id}
          item={{
            slug: prefixSlug(
              playEpisode.fields.slug,
              playEpisode.sys.contentType.sys.id,
            ),
            title: playEpisode.fields.title,
            image: playEpisode.fields.image,
            categoryName: playShow?.fields.title,
            categoryColor: "Play",
            fulldate: !props.groupByDate,
            createdAt:
              playEpisode.fields.broadcastStart || playEpisode.sys.createdAt,
            type: playEpisode.sys.contentType.sys.id,
          }}
        />
      );
    }

    if (item.sys.contentType.sys.id === "podcast-episode") {
      const podcastEpisode = item as Content.PodcastEpisode;
      const podcastShow = podcastEpisode.fields["podcast-show"];
      return (
        <Item
          key={podcastEpisode.sys.id}
          item={{
            slug: prefixSlug(
              podcastEpisode.fields.slug,
              podcastEpisode.sys.contentType.sys.id,
            ),
            categoryName: podcastShow?.fields.title,
            categoryColor: "Podd",
            fulldate: !props.groupByDate,
            title: podcastEpisode.fields.title,
            excerpt: podcastEpisode.fields.excerpt,
            image: podcastEpisode.fields.image,
            createdAt: podcastEpisode.sys.createdAt,
            type: podcastEpisode.sys.contentType.sys.id,
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <div className="divide-y divide-sand-700 " ref={feedRef}>
        {groupedItems ? (
          Object.keys(groupedItems)
            .sort((a, b) => b.localeCompare(a))
            .map((date) => (
              <div className="py-7 md:py-14" key={date}>
                <h2 className="font-bold text-xl font-noto-sans bg-sand-600 p-4 rounded-sm">
                  <FormatDate date={date} format="relative" />
                </h2>
                <ul className="divide-y divide-sand-700 ">
                  {groupedItems[date].map(renderItem)}
                </ul>
              </div>
            ))
        ) : (
          <ul className="divide-y divide-sand-700 ">{items.map(renderItem)}</ul>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center pt-8 pb-4">
          <button
            type="button"
            onClick={fetchItems}
            aria-disabled={isLoading}
            aria-busy={isLoading}
            className="bg-sand-700 btn aria-disabled:opacity-50"
          >
            {isLoading ? "Laddar..." : "Ladda fler"}
          </button>
        </div>
      )}
    </>
  );
};

export default ItemFeed;
